import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

type HospitalMeta = { id: string; name: string; address?: string; contactNumber?: string; email?: string };

export default function PublicSelfRegister() {
  const [sp] = useSearchParams();
  const hospitalId = sp.get("h") ?? "";
  const refFromUrl = sp.get("ref") ?? "";

  const { data: hospital, isLoading } = useQuery({
    queryKey: ["self-register-hospital", hospitalId],
    queryFn: async () => {
      const res = await api.get<{ data: HospitalMeta }>(`/api/public/self-register/hospitals/${hospitalId}/meta`);
      return res.data?.data;
    },
    enabled: !!hospitalId,
  });

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [pastDiseases, setPastDiseases] = useState("");
  const [dob, setDob] = useState<string>("");
  const [sex, setSex] = useState<"MALE" | "FEMALE" | "OTHER">("OTHER");
  const [files, setFiles] = useState<File[]>([]);
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [referralValid, setReferralValid] = useState<string | null>(null);

  useEffect(() => {
    if (!hospitalId || !refFromUrl) return;
    setReferralCode(refFromUrl);
    api
      .get<{ data: { valid: boolean; referrerName?: string } }>(
        `/api/public/referral/validate?hospitalId=${encodeURIComponent(hospitalId)}&code=${encodeURIComponent(refFromUrl)}`
      )
      .then((r) => {
        if (r.data?.data?.valid) {
          setReferralValid(r.data.data.referrerName ?? "Valid code");
        } else {
          setReferralValid("Invalid code");
        }
      })
      .catch(() => setReferralValid(null));
  }, [hospitalId, refFromUrl]);

  const canSubmit = useMemo(() => {
    const ph = phone.replace(/\D/g, "").slice(-10);
    return !!hospitalId && name.trim().length >= 2 && /^\d{10}$/.test(ph);
  }, [hospitalId, name, phone]);

  const registerMutation = useMutation({
    mutationFn: async () => {
      const ph = phone.replace(/\D/g, "").slice(-10);
      const reg = await api.post<{ data: { patientId: string; uhid?: string; patientUniqueId?: string } }>(
        "/api/public/self-register",
        {
          hospitalId,
          name: name.trim(),
          phone: ph,
          email: email.trim() || undefined,
          address: address.trim() || undefined,
          pastDiseases: pastDiseases.trim() || undefined,
          dob: dob || undefined,
          sex: sex || undefined,
          referralCode: referralCode.trim() || undefined,
        }
      );
      const patientId = (reg.data as any)?.data?.patientId as string;
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("hospitalId", hospitalId);
        files.forEach((f) => fd.append("files", f));
        try {
          await api.post(`/api/public/self-register/${patientId}/reports`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e: any) {
          // Patient is already registered; report upload can be retried later by staff.
          toast.error("Registered, but report upload failed. Please share reports with hospital reception.");
        }
      }
      return reg;
    },
    onSuccess: (res: any) => {
      const d = (res?.data as any)?.data ?? {};
      const extra = d.referralMessage ? ` — ${d.referralMessage}` : "";
      toast.success(`Registered successfully${d.uhid ? ` (UHID: ${d.uhid})` : ""}${extra}`);
      setName("");
      setPhone("");
      setEmail("");
      setAddress("");
      setPastDiseases("");
      setDob("");
      setSex("OTHER");
      setFiles([]);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Patient Self Registration</CardTitle>
          <div className="text-sm text-muted-foreground">
            {isLoading ? "Loading hospital..." : hospital ? `Hospital: ${hospital.name}` : hospitalId ? "Invalid hospital link" : "Missing hospital link"}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Name *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Referral code (optional)</label>
              <Input
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Friend's referral code"
              />
              {referralValid && (
                <p className={`text-xs ${referralValid === "Invalid code" ? "text-red-600" : "text-teal-700"}`}>
                  {referralValid === "Invalid code" ? "Invalid referral code" : `Referred by ${referralValid}`}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Phone (10 digits) *</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">DoB</label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Sex</label>
              <Select value={sex} onValueChange={(v) => setSex(v as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sex" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MALE">Male</SelectItem>
                  <SelectItem value="FEMALE">Female</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Address</label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Past diseases / history</label>
            <Textarea value={pastDiseases} onChange={(e) => setPastDiseases(e.target.value)} rows={3} placeholder="Optional" />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Upload reports (optional)</label>
            <Input
              type="file"
              multiple
              accept=".pdf,image/*"
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
            {files.length > 0 && (
              <div className="text-xs text-muted-foreground">{files.length} file(s) selected</div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => registerMutation.mutate()} disabled={!canSubmit || registerMutation.isPending}>
              {registerMutation.isPending ? "Submitting..." : "Register"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

